import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import { CircularProgress } from "@mui/material";

import { useQuery } from "@tanstack/react-query";

import { ResourceError } from "@features/errors";
import { clearOrderVariantErrors } from "@redux/slices/orders/orderSetSlice";

import {
  CurrentOrderSetContext,
  OrderSetContextType,
} from "./CurrentOrderSetContext";
import { combineOrderSetAndOrders } from "./combineOrderSetAndOrders";
import { orderSetsKeyFactory } from "./data";
import useOrderSetId from "./useOrderSetId";

const CurrentOrderSetContextProvider = ({
  children,
  fallback,
  onOrderSetDelete,
}: {
  children: React.ReactNode;
  fallback?: React.JSX.Element;
  onOrderSetDelete: OrderSetContextType["onOrderSetDelete"];
}) => {
  const dispatch = useDispatch();
  const orderSetId = useOrderSetId();
  const orderSetQuery = useQuery({
    ...orderSetsKeyFactory.detail(orderSetId),
    enabled: !!orderSetId,
    staleTime: 500,
  });

  const ordersQuery = useQuery({
    ...orderSetsKeyFactory.detail(orderSetId)._ctx.orders,
    enabled: !!orderSetId,
    staleTime: 500,
  });

  useEffect(() => {
    if (!ordersQuery.isFetching) {
      dispatch(clearOrderVariantErrors());
    }
  }, [dispatch, ordersQuery?.isFetching]);

  const orderSetData = useMemo(
    () => combineOrderSetAndOrders(orderSetQuery.data, ordersQuery.data),
    [orderSetQuery.data, ordersQuery.data]
  );

  const error = orderSetQuery.error || ordersQuery.error;
  if (error) {
    return <ResourceError error={error} />;
  }

  if (orderSetQuery.isPending || ordersQuery.isPending || !orderSetData) {
    return fallback || <CircularProgress />;
  }

  return (
    <CurrentOrderSetContext.Provider
      value={{
        orderSetId,
        orderSet: orderSetData.orderSet,
        orders: orderSetData.orders,
        orderSetVariants: orderSetData.orderSetVariants,
        isFetching: orderSetQuery.isFetching || ordersQuery.isFetching,
        onOrderSetDelete,
      }}
    >
      {children}
    </CurrentOrderSetContext.Provider>
  );
};

export default CurrentOrderSetContextProvider;
